// The My Profile → Notifications tab's own state (BL-1230).
//
// Split out of `useProfileModal` rather than living inside it: that hook already
// owns three unrelated concerns (profile form, password change, tab state), and
// folding a fourth in pushed it past the 100-line limit. The tab's data has no
// relationship to the profile form's — different endpoint, different lifecycle,
// different error surface — so it gets its own hook and `useProfileModal` just
// spreads the result.
'use client';

import { useCallback, useEffect, useState } from 'react';

import type { StaffNotificationPreferenceDto } from '@auctogon/core-admin/schemas/responses/staff-notification-preferences';

import {
  fetchNotificationPreferences,
  saveNotificationPreferences,
} from './profile-modal-helpers';

/** What the profile modal needs to render + drive the notifications tab. */
export interface UseStaffNotificationPreferencesResult {
  /** Eligible toggles for this user at the active tenant. Empty is valid. */
  notifications: StaffNotificationPreferenceDto[];
  /** True while the initial load is in flight. */
  isLoadingNotifications: boolean;
  /** True while a save is in flight. */
  isSavingNotifications: boolean;
  /** Load or save failure, surfaced inline on the tab. */
  notificationsError: string | null;
  /** Success message after a save, cleared by the caller's timer. */
  notificationsSuccess: string | null;
  /** Flip one toggle locally. Nothing is written until save. */
  handleToggleNotification: (eventType: string, channel: string, enabled: boolean) => void;
  /** Persist every toggle in one request. */
  handleSaveNotifications: () => Promise<void>;
}

/**
 * Load and save the signed-in staff user's own notification switches.
 *
 * Keyed on the ACTIVE tenant: the same user can be opted in at one auction house
 * and not another (BL-1230 AC 7), so switching tenants re-reads.
 *
 * @param args - whether the modal is open, and the active tenant id.
 * @returns the tab's state plus its toggle + save handlers.
 * @sideEffect `handleSaveNotifications` upserts `staff_notification_opt_ins` preference rows.
 */
export function useStaffNotificationPreferences(args: {
  isOpen: boolean;
  tenantId: string | undefined;
}): UseStaffNotificationPreferencesResult {
  const { isOpen, tenantId } = args;

  // Empty until the fetch lands — and empty is ALSO the legitimate steady state
  // for a role with no eligible alerts. The tab distinguishes the two via
  // `isLoadingNotifications`.
  const [notifications, setNotifications] = useState<StaffNotificationPreferenceDto[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);
  const [notificationsSuccess, setNotificationsSuccess] = useState<string | null>(null);

  // Auto-clear the success message after 3 seconds — same convention (and the
  // same interval) as the Profile tab's banner in `useProfileModal`, so the two
  // tabs of one modal don't confirm saves differently. Without this the
  // confirmation outlives the save it describes and goes stale on screen.
  useEffect(() => {
    if (!notificationsSuccess) return;
    const timer = setTimeout(() => setNotificationsSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [notificationsSuccess]);

  /** Load the caller's preferences. Bails if the modal closed mid-flight. */
  const load = useCallback(async (activeTenantId: string, isStale: () => boolean) => {
    setIsLoadingNotifications(true);
    setNotificationsError(null);
    try {
      const prefs = await fetchNotificationPreferences(activeTenantId);
      if (!isStale()) setNotifications(prefs);
    } catch (err) {
      // Surface it — never fall back to a guessed default, which would render a
      // toggle state the server never sent (localstorage-judicious.md's
      // stale-cache failure mode, one layer up).
      if (!isStale()) {
        setNotificationsError(
          err instanceof Error ? err.message : 'Failed to load notification settings',
        );
      }
    } finally {
      if (!isStale()) setIsLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !tenantId) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-open: `load` sets loading/data/error state, which is exactly the "subscribe to an external system" shape the rule sanctions; the linter can't see through the async fn. The cancelled flag guards the unmount race.
    void load(tenantId, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [isOpen, tenantId, load]);

  const handleToggleNotification = useCallback(
    (eventType: string, channel: string, enabled: boolean) => {
      setNotifications((prev) =>
        prev.map((p) =>
          p.eventType === eventType && p.channel === channel && !p.locked ? { ...p, enabled } : p,
        ),
      );
    },
    [],
  );

  /**
   * Persist every toggle in one request, then re-render from the canonical
   * response. The modal stays OPEN on failure with the error inline — closing it
   * would hide the message it describes (error-handling-chain.md).
   */
  const handleSaveNotifications = useCallback(async () => {
    if (!tenantId) return;
    setIsSavingNotifications(true);
    setNotificationsError(null);
    // Clear any prior confirmation up front, so a second save can't leave the
    // previous run's banner on screen while this one is still in flight.
    setNotificationsSuccess(null);
    try {
      const saved = await saveNotificationPreferences(tenantId, notifications);
      setNotifications(saved);
      setNotificationsSuccess('Notification settings updated');
    } catch (err) {
      setNotificationsError(
        err instanceof Error ? err.message : 'Failed to save notification settings',
      );
    } finally {
      setIsSavingNotifications(false);
    }
  }, [tenantId, notifications]);

  return {
    notifications,
    isLoadingNotifications,
    isSavingNotifications,
    notificationsError,
    notificationsSuccess,
    handleToggleNotification,
    handleSaveNotifications,
  };
}
